import React, { useState } from 'react';
import ImageUploader from './ImageUploader';
import { UserCircleIcon, BuildingOfficeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface MultiImageUploaderProps {
  onImagesUpdated: (profileImage: string | null, companyLogo: string | null) => void;
}

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({ onImagesUpdated }) => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [activeUpload, setActiveUpload] = useState<'profile' | 'logo' | null>(null);

  const handleImageCropped = (image: string) => {
    if (activeUpload === 'profile') {
      setProfileImage(image);
      onImagesUpdated(image, companyLogo);
    } else if (activeUpload === 'logo') {
      setCompanyLogo(image);
      onImagesUpdated(profileImage, image);
    }
    setActiveUpload(null);
  };

  const handleRemoveImage = (imageType: 'profile' | 'logo') => {
    if (imageType === 'profile') {
      setProfileImage(null);
      onImagesUpdated(null, companyLogo);
    } else {
      setCompanyLogo(null);
      onImagesUpdated(profileImage, null);
    }
  };

  if (activeUpload) {
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">
          Upload {activeUpload === 'profile' ? 'Your Photo' : 'Company Logo'}
        </h3>
        <ImageUploader 
          onImageCropped={handleImageCropped}
          mode={activeUpload}
        />
        <button
          onClick={() => setActiveUpload(null)}
          className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Add your photo and/or company logo to personalize your social card
      </p>

      {/* Profile Photo Section */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCircleIcon className="h-8 w-8 text-gray-400" />
            <div>
              <h4 className="font-medium text-gray-900">Your Photo</h4>
              <p className="text-sm text-gray-500">
                {profileImage ? 'Photo uploaded' : 'Add your profile photo'}
              </p>
            </div>
          </div>
          {profileImage ? (
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <button
                onClick={() => handleRemoveImage('profile')}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              onClick={() => setActiveUpload('profile')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Add Photo
            </button>
          )}
        </div>
      </div>

      {/* Company Logo Section */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
            <div>
              <h4 className="font-medium text-gray-900">Company Logo</h4>
              <p className="text-sm text-gray-500">
                {companyLogo ? 'Logo uploaded' : 'Add your company logo (optional)'}
              </p>
            </div>
          </div>
          {companyLogo ? (
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <button
                onClick={() => handleRemoveImage('logo')}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              onClick={() => setActiveUpload('logo')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Add Logo
            </button>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <span className="font-medium">Tip:</span> You can add just your photo, just your company logo, or both!
        </p>
      </div>
    </div>
  );
};

export default MultiImageUploader;